import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import Card from "../../components/common/Card";
import Table from "../../components/common/Table";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import { useGetOrderLifecycleQuery } from "../../store/api";

const OrderLifecycleDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const from = searchParams.get("from") || undefined;
  const to = searchParams.get("to") || undefined;
  const channel = searchParams.get("channel") || undefined;

  const queryArgs = useMemo(
    () => ({
      from,
      to,
      channel,
    }),
    [from, to, channel],
  );

  const {
    data: lifecycle,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetOrderLifecycleQuery(queryArgs, { skip: !orderId });

  const fallbackOrder = location.state?.order || null;
  const fetchedOrder = (lifecycle?.orders || []).find((order) => order.orderId === orderId);
  const order = fetchedOrder || fallbackOrder;

  const toText = (value) =>
    value === null || value === undefined || value === "" ? "N/A" : value;
  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
  };
  const isTakeaway =
    String(order?.orderType || "").toUpperCase() === "TAKEAWAY";

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Order Lifecycle Detail${order?.orderNo ? ` ${order.orderNo}` : ""}`}
        subtitle="Detailed order, token, and item breakdown."
        actions={
          <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      {isLoading && (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading order detail...
          </p>
        </Card>
      )}

      {isError && (
        <Alert tone="error">
          <div className="flex items-center justify-between gap-3">
            <span>
              {error?.data?.detail ||
                error?.data?.message ||
                "Failed to load order detail."}
            </span>
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {!isLoading && !isError && !order && (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Order not found for selected filters.
          </p>
        </Card>
      )}

      {!isLoading && !isError && order && (
        <Card className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Order No
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.orderNo)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Placed At
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {formatDateTime(order.placedAt)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Grand Total
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {formatMoney(order.grandTotal)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Channel
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.channel)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Order Type
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.orderType)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Status
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.status)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Payment Status
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.paymentStatus)}
              </p>
            </div>
            {isTakeaway && (
              <>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Customer
                  </p>
                  <p className="text-sm text-slate-900 dark:text-slate-100">
                    {toText(order.customerName)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Phone
                  </p>
                  <p className="text-sm text-slate-900 dark:text-slate-100">
                    {toText(order.customerPhone)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Email
                  </p>
                  <p className="text-sm text-slate-900 dark:text-slate-100">
                    {toText(order.customerEmail)}
                  </p>
                </div>
              </>
            )}
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Table
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.tableNo)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Master Token
              </p>
              <p className="text-sm text-slate-900 dark:text-slate-100">
                {toText(order.masterTokenNo)}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <h4 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              All Order Items
            </h4>
            <Table
              columns={[
                { key: "itemName", label: "Item" },
                { key: "qty", label: "Qty" },
                { key: "unitPrice", label: "Unit Price" },
                { key: "lineTotal", label: "Line Total" },
                { key: "stationCode", label: "Station" },
                { key: "notes", label: "Notes" },
              ]}
              rows={(order.items || []).map((item) => ({
                id: item.orderLineId,
                itemName: toText(item.itemName),
                qty: Number(item.qty || 0),
                unitPrice: formatMoney(item.unitPrice),
                lineTotal: formatMoney(item.lineTotal),
                stationCode: toText(item.stationCode),
                notes: toText(item.notes),
              }))}
              emptyText="No items found for this order."
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default OrderLifecycleDetail;
